// Shared utility functions

/**
 * Generate hash from string (used for password hashing)
 * @param {string} str - Input string
 * @returns {string} Hash value
 */
export function genHash(str) {
  if (!str) return '';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash += str.charCodeAt(i);
  }
  return Math.abs(hash).toString();
}

/**
 * Validate email format
 * @param {string} email - Email address
 * @returns {boolean} True if valid
 */
/**
 * THE email shape check. One rule, imported everywhere — it previously existed six
 * times in two different versions, and THIS canonical copy was the weak one:
 * `includes('@') && includes('.')`, which accepts `a.b@c` (no dot after the @),
 * `two@@x.com` and `sp ace@x.com`. Both client apps shadowed it with their own
 * stricter copy under the SAME function name, so `isValidEmail` meant different things
 * depending on which module you imported.
 *
 * The regex below is the one four of those six sites already used, so this tightens
 * the outlier rather than inventing a new rule.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: exclude SQL LIKE metacharacters. `%@%.%` and
 * `a_b@x.com` both pass, and should. `_` is ordinary in real addresses, and the reason
 * a wildcard-shaped email was once dangerous — account lookups using ILIKE, where the
 * stored value acted as a pattern — was fixed at the lookup (LOWER() = LOWER()), which
 * is where it belonged. Rejecting `%` here would be theatre that also breaks nothing
 * an attacker relies on.
 *
 * Intentionally permissive about the rest: full RFC 5322 is famously not worth
 * enforcing with a regex, and the authoritative check is whether the verification email
 * arrives.
 */
export function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email));
}

/**
 * Check if string contains only single-byte characters
 * @param {string} str - Input string
 * @returns {boolean} True if single-byte only
 */
export function isSingleByte(str) {
  if (!str) return true;
  return /^[\x00-\xFF]*$/.test(str);
}

/**
 * Format date to ISO 8601 string
 * @param {Date} date - Date object
 * @returns {string} ISO formatted date string
 */
export function formatDateISO(date = new Date()) {
  return date.toISOString();
}

/**
 * Insert-or-replace an entry by an arbitrary key field in a plain JSONB-array
 * shape — the general form of the "filter out the old one, push the new one"
 * pattern used across `tags`/`webhooks`/similar arrays keyed by SOME field
 * (`tag`, `name`, `event_criterion`, …), not always `{tag,value}`. A plain
 * array spread on repeat writes accumulates duplicate keys instead of
 * replacing the old entry — silently stale data for any reader that does
 * `list.find(x => x[keyField] === X)` (first match wins), even though the
 * fresh value is sitting right there as a later entry.
 *
 * Pass `entry: null`/`undefined` to remove the keyed entry without replacing
 * it (e.g. clearing an optional webhook) — the filter still runs, nothing is
 * pushed back.
 *
 * @param {Array<Object>} list - existing array (not mutated)
 * @param {string} keyField - the property name that identifies an entry (e.g. 'tag', 'name', 'event_criterion')
 * @param {*} keyValue - the key value to upsert
 * @param {Object|null|undefined} entry - the replacement entry, or null/undefined to just remove
 * @returns {Array<Object>} a new array with the keyed entry inserted/replaced/removed
 */
export function upsertByKey(list, keyField, keyValue, entry) {
  const out = (Array.isArray(list) ? list : []).filter((item) => !(item && item[keyField] === keyValue));
  if (entry) out.push(entry);
  return out;
}

/**
 * Insert-or-replace a tag by key in the `[{tag,value}]` JSONB shape used across
 * every table's `tags` column (coreMeta, sseEther*, m5mLead, m5mMagnetEvent, …).
 * Found via m5mLead's `scheduler_timezone` tag duplicating on a resubmission-
 * reconciliation merge; use this wherever a tag needs to reflect the LATEST
 * value rather than accumulate history. A thin, `{tag,value}`-shaped wrapper
 * around the general upsertByKey() above.
 * @param {Array<{tag:string,value:*}>} tags - existing tags array (not mutated)
 * @param {string} tag - the tag key to upsert
 * @param {*} value - the new value for that key
 * @returns {Array<{tag:string,value:*}>} a new array with the key inserted/replaced
 */
export function upsertTag(tags, tag, value) {
  return upsertByKey(tags, 'tag', tag, { tag, value });
}

/**
 * Generate UUID v4
 * @returns {string} UUID
 */
export function generateGUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}
